import { HTTPError } from "ky";

/** One entry of API-Server's Zod validation `details` array (see `app/control/v1/phoneRoutes.ts`). */
interface ApiErrorDetail {
    field?: string;
    message?: string;
}

/** API error body shape: optional code + message (aligns with other API responses). */
interface ApiErrorBody {
    code?: string;
    message?: string;
    error?: string;
    /**
     * Present on every 400 from `phoneRouter`, which answers a failed `safeParse` with
     * `{ error: "Validation failed", details: [{ field, message }] }`. Without reading this the
     * toast says literally "Validation failed" and names nothing the vet could act on.
     */
    details?: ApiErrorDetail[];
}

/** User-facing messages for known phone API error codes or conditions. */
const PHONE_ERROR_MESSAGES: Record<string, string> = {
    DUPLICATE_PHONE_NUMBER:
        "This phone number is already on your account. Use a different number or edit the existing one.",
    duplicate_phone: "This phone number is already on your account. Use a different number or edit the existing one.",
};

// Deliberately does NOT include "Failed to create phone number record". That is the generic 500
// this route returns for ANY non-23505 insert failure (missing column, lost connection, RLS), so
// treating it as a duplicate rendered a schema or infrastructure fault as "this number is already
// on your account" — the vet then tries a different number, gets the same message, and concludes
// their own data is broken. Real duplicates come back as a 409 with the server's own copy
// (`accountPhoneNumberController.ts`, errorCode 23505), which the 409 branch below already
// handles, so the substring earned nothing. Pinned by "a generic 500 is not reported as a
// duplicate" in `src/__tests__/common/utils/phoneApiErrors.spec.ts`.
const DUPLICATE_MESSAGE_SUBSTRINGS = ["duplicate", "already exists", "unique"];

const FALLBACK_MESSAGE = "Failed to save number. Please try again.";

/**
 * Folds a validation `details` array onto the server's summary line, so a 400 names the field that
 * was actually rejected instead of just "Validation failed". Entries missing a `field` contribute
 * their message alone; entries missing a `message` are dropped, since a bare field name says
 * nothing. Returns the summary unchanged when there is nothing usable to add.
 */
const withValidationDetails = (summary: string, details: ApiErrorDetail[] | undefined): string => {
    if (!Array.isArray(details)) return summary;
    const parts = details
        .map(({ field, message }) => {
            const text = (message ?? "").trim();
            if (!text) return "";
            const name = (field ?? "").trim();
            return name ? `${name}: ${text}` : text;
        })
        .filter(Boolean);
    if (parts.length === 0) return summary;
    const joined = parts.join("; ");
    return summary ? `${summary} — ${joined}` : joined;
};

/**
 * Returns a user-friendly message for phone add/update API errors.
 * Uses ky's HTTPError: reads response body for code/message. Treats 409 Conflict
 * as duplicate (account_id, phone_number, type); uses server message when present.
 */
export async function getPhoneApiErrorMessage(error: unknown): Promise<string> {
    if (error instanceof HTTPError) {
        try {
            const body = (await error.response
                .clone()
                .json()
                .catch(() => null)) as ApiErrorBody | null;
            const code = body?.code;
            // Details are folded in before any branch reads the message, so every path that
            // surfaces a server message benefits — not only this feature's 400s.
            const serverMessage = withValidationDetails((body?.message ?? body?.error ?? "").trim(), body?.details);

            if (code && PHONE_ERROR_MESSAGES[code]) {
                return PHONE_ERROR_MESSAGES[code];
            }
            if (error.response.status === 409) {
                return serverMessage || PHONE_ERROR_MESSAGES.DUPLICATE_PHONE_NUMBER;
            }
            if (
                serverMessage &&
                DUPLICATE_MESSAGE_SUBSTRINGS.some((s) => serverMessage.toLowerCase().includes(s.toLowerCase()))
            ) {
                return PHONE_ERROR_MESSAGES.DUPLICATE_PHONE_NUMBER;
            }
            if (serverMessage) {
                return serverMessage;
            }
        } catch {
            // ignore parse errors
        }
        return FALLBACK_MESSAGE;
    }
    if (error instanceof Error && error.message?.trim()) {
        if (DUPLICATE_MESSAGE_SUBSTRINGS.some((s) => error.message.toLowerCase().includes(s.toLowerCase()))) {
            return PHONE_ERROR_MESSAGES.DUPLICATE_PHONE_NUMBER;
        }
        return error.message.trim();
    }
    return FALLBACK_MESSAGE;
}
